


import {
  defineComponent,
  computed,
  onMounted,
  watch,
  onUnmounted,
  PropType,
} from '@vue/composition-api';

import RealtimeModePane from '@/components/ExternalReports/Standard/components/RealtimeView/RealtimeModePane/index.vue';
import PhotoModal from '@/components/lib/PhotoModal/index.vue';
import MapMiscInfo from '@/components/ExternalReports/Standard/components/common/MapMiscInfo.vue';
import type { StandardMtxExt } from '@/models/externalReports/standard/standardMtx';
import type { StandardReportPhotoExt } from '@/models/externalReports/standard/standardReport';
import type { StandardCarExt } from '@/models/externalReports/standard/standardCar';
import ManualDownloadLinks from '@/components/ExternalReports/Standard/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/Standard/composables/usePaneControl';
import { useExtremeMap } from '@/components/ExternalReports/Standard/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/Standard/composables/usePhotoModal';
import {
  CAR_LIST_MAX_HEIGHT_RATE,
  CAR_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/Standard/consts';
import { fetchImageAsObjectURL, requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';
import { useRequestGuard } from '@/composables/useRequestGuard';
import useStandardCars from '@/components/ExternalReports/Standard/composables/useCars';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  setup(props) {
    const {
      paneState,
      onChangeShowCarIcons: onChangeShowCarIconsOrig,
      onChangePhotoTypeVisibility: onChangePhotoTypeVisibilityOrig,
    } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: CAR_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: CAR_LIST_MAX_HEIGHT_RATE,
      },
    });

    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      redrawCarLayer,
      showStandardCarPopup,
      showStandardReportPhotoPopup,
      showStandardMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);

    const { doRequest } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const {
      carState,
      startCarUpdateInterval,
      stopCarUpdateInterval,
      selectCar,
      deselectCar,
      deselectSelectedCarReportPhotos,
      redrawCarLayerWatchValue,
    } = useStandardCars();

    watch(
      redrawCarLayerWatchValue,
      (value, oldValue) => {
        const fitToExtent = oldValue === 0 || oldValue === undefined;
        redrawStandardCarLayer({ fitToExtent });
      },
    );

    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
    } = usePhotoModal();
    const showPhotoModal = async(photoType: string, photos: StandardReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      showPhotoModalOrig(photoType, photos);
    };

    const movingCarsCount = computed<number>(() => {
      return carState.cars.filter(car => car.isMoving).length;
    });
    const stoppedCarsCount = computed<number>(() => {
      return carState.cars.filter(car => !car.isMoving).length;
    });

    interface RedrawStandardCarLayerOptions {
      fitToExtent?: boolean;
      setSelectedCarToCenter?: boolean;
    }
    const redrawStandardCarLayer = (options: RedrawStandardCarLayerOptions = {}) => {
      redrawCarLayer(options);
    };

    const onChangeShowCarIcons = () => {
      onChangeShowCarIconsOrig();
      redrawStandardCarLayer();
    };
    const onChangePhotoTypeVisibility = (photoType: string, isVisible: boolean) => {
      onChangePhotoTypeVisibilityOrig(photoType, isVisible);
      redrawStandardCarLayer();
    };

    const onClickCar = async(selectedCar: StandardCarExt) => {
      hidePopup();
      // 選択中の車両をクリックした場合、選択を解除する
      if (deselectCar(selectedCar.id)) {
        redrawStandardCarLayer();
        return;
      }
      await doRequest(async() => {
        await selectCar(selectedCar.id);
      }, '車両の取得に失敗しました。');
      const updated = carState.cars.find(c => c.id === selectedCar.id);
      showStandardCarPopup(updated ?? selectedCar);
      redrawStandardCarLayer({ setSelectedCarToCenter: true });
    };

    const onClickPhotoPin = async(photo: StandardReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }
        showStandardReportPhotoPopup(photo);
      }
      redrawStandardCarLayer();
    };
    const onClickMtx = (mtx: StandardMtxExt) => {
      const isSelected = mtx.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showStandardMtxPopup(mtx);
      }
      redrawStandardCarLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedCarReportPhotos();
      redrawStandardCarLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    onMounted(async() => {
      await doRequest(async() => {
        await loadExtremeMapEssentials();
        await startCarUpdateInterval(props.userSettings);
      }, '車両の取得に失敗しました。');
    });

    onUnmounted(() => {
      stopCarUpdateInterval();
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      mapViewState,
      paneState,
      carState,
      photoModalState,
      // ref
      extremeMapRef,
      // methods
      onClickCar,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      onChangeShowCarIcons,
      onChangePhotoTypeVisibility,
      paneResizeStopped,
      showPhotoModal,
      hidePhotoModal,
      movingCarsCount,
      stoppedCarsCount,
    };
  },
  components: {
    RealtimeModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
